/**
 * LocationLeaderService
 * Service for LocationLeader entity operations
 * Route: api/location-leaders
 * Controller: LocationLeadersController.cs
 *
 * Composite key (LocationID + MemberID): this service deliberately does NOT extend CrudService, so the
 * single-id getById / update / delete that would hit the wrong routes do not exist. At most one primary
 * leader per location: the first leader becomes primary and flagging another clears the previous one.
 */
import { BaseService } from '../base/BaseService';
/**
 * LocationLeaderService
 * Handles location leader operations by composite key
 * Maps to /api/location-leaders endpoint
 */
export class LocationLeaderService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? LocationLeaderService.DEFAULT_SERVICE_PATH,
            basePath: LocationLeaderService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    /**
     * All leader assignments
     * GET /api/location-leaders
     */
    async getAll() {
        const response = await this.getAllRaw();
        return response.data ?? [];
    }
    getAllRaw() {
        return this.http.get(this.buildUrl());
    }
    /**
     * Get all leaders for a specific location (primary first)
     * GET /api/location-leaders/location/{locationId}
     * @param locationId - Location ID
     * @returns Array of location leaders
     */
    async getByLocationId(locationId) {
        const response = await this.getByLocationIdRaw(locationId);
        return response.data ?? [];
    }
    /**
     * Get all leaders for a specific location (raw)
     * @param locationId - Location ID
     * @returns ResponseData with array of location leaders
     */
    getByLocationIdRaw(locationId) {
        const endpoint = this.getEndpoint('byLocationId', `location/${locationId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Every leader with the member's name, e-mail, phone and avatar resolved server-side.
     * Needs only Location.Leader.View (not Member.Read).
     * GET /api/location-leaders/view
     */
    async getAllViews() {
        const response = await this.getAllViewsRaw();
        return response.data ?? [];
    }
    getAllViewsRaw() {
        const endpoint = this.getEndpoint('views', 'view');
        return this.http.get(this.buildUrl(endpoint));
    }
    /**
     * Leaders of a location with member names (primary first). Needs only Location.Leader.View.
     * GET /api/location-leaders/location/{locationId}/view
     * @param locationId - Location ID (0 is the Online Campus)
     */
    async getViewsByLocationId(locationId) {
        const response = await this.getViewsByLocationIdRaw(locationId);
        return response.data ?? [];
    }
    getViewsByLocationIdRaw(locationId) {
        const endpoint = this.getEndpoint('viewsByLocationId', `location/${locationId}/view`);
        return this.http.get(this.buildUrl(endpoint));
    }
    /**
     * Get a location leader by composite key (LocationID + MemberID)
     * GET /api/location-leaders/{locationId}/{memberId}
     * @param locationId - Location ID
     * @param memberId - Member ID
     * @returns Location leader
     */
    async getByCompositeKey(locationId, memberId) {
        const response = await this.getByCompositeKeyRaw(locationId, memberId);
        if (!response.data) {
            throw new Error(`Location leader (Location: ${locationId}, Member: ${memberId}) not found`);
        }
        return response.data;
    }
    /**
     * Get a location leader by composite key (raw)
     * @param locationId - Location ID
     * @param memberId - Member ID
     * @returns ResponseData with location leader
     */
    getByCompositeKeyRaw(locationId, memberId) {
        const endpoint = this.getEndpoint('byCompositeKey', `${locationId}/${memberId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Add a leader to a location (409 leader_exists when already assigned)
     * POST /api/location-leaders
     * @param request - { locationId, memberId, isPrimary? }
     * @returns Created location leader
     */
    async addLeader(request) {
        const response = await this.addLeaderRaw(request);
        if (!response.data) {
            throw new Error('Failed to add location leader');
        }
        return response.data;
    }
    /**
     * Add a leader to a location (raw)
     * @param request - Add location leader request
     * @returns ResponseData with created location leader
     */
    addLeaderRaw(request) {
        const endpoint = this.getEndpoint('create', '');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, request);
    }
    /**
     * Change a leader's primary flag (true clears the other primary)
     * PUT /api/location-leaders/{locationId}/{memberId}
     * @param locationId - Location ID
     * @param memberId - Member ID
     * @param request - { isPrimary } (a full LocationLeader also works)
     * @returns Updated location leader
     */
    async updateByCompositeKey(locationId, memberId, request) {
        const response = await this.updateByCompositeKeyRaw(locationId, memberId, request);
        if (!response.data) {
            throw new Error(`Failed to update location leader (Location: ${locationId}, Member: ${memberId})`);
        }
        return response.data;
    }
    /**
     * Update a location leader assignment (raw)
     */
    updateByCompositeKeyRaw(locationId, memberId, request) {
        const endpoint = this.getEndpoint('update', `${locationId}/${memberId}`);
        const url = this.buildUrl(endpoint);
        return this.http.put(url, request);
    }
    /**
     * Make a leader the primary (clears the previous primary)
     */
    setPrimary(locationId, memberId) {
        return this.updateByCompositeKey(locationId, memberId, { isPrimary: true });
    }
    /**
     * Remove a leader from a location
     * DELETE /api/location-leaders/{locationId}/{memberId}
     * @param locationId - Location ID
     * @param memberId - Member ID
     * @returns True if removed successfully
     */
    async removeLeader(locationId, memberId) {
        const response = await this.removeLeaderRaw(locationId, memberId);
        return response.success;
    }
    /**
     * Remove a leader from a location (raw)
     * @param locationId - Location ID
     * @param memberId - Member ID
     * @returns ResponseData
     */
    removeLeaderRaw(locationId, memberId) {
        const endpoint = this.getEndpoint('delete', `${locationId}/${memberId}`);
        const url = this.buildUrl(endpoint);
        return this.http.delete(url);
    }
}
LocationLeaderService.DEFAULT_SERVICE_PATH = 'locations';
LocationLeaderService.BASE_PATH = 'api/location-leaders';
//# sourceMappingURL=LocationLeaderService.js.map